import React, { useRef, useMemo, useState } from 'react';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { useDndContext, useDndMonitor, useDroppable } from '@dnd-kit/core';
import {
    SortableContext, useSortable, verticalListSortingStrategy, rectSwappingStrategy, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { FaGripVertical, FaTimes, FaEllipsisV, FaCheck, FaLayerGroup, FaTrash, FaLink } from 'react-icons/fa';

const BRAND_NAVY   = '#1f3c88';
const BRAND_BLUE   = '#2d7ff9';
const BRAND_SKY    = '#eef6ff';
const BRAND_BORDER = '#dbe8f8';

// Realce de "juntar" (hover de outro draggable sobre a célula ativa): a borda azul fina (1px,
// via CSS border) + só um pouco de sombra suave (sem anel grosso). 2d7ff9 = 45,127,249.
const REALCE_SHADOW = '0 0 6px 0 rgba(45, 127, 249, 0.45)';

// Altura de uma célula COLAPSADA por nº de trechos (chip único = 1 linha; grupo =
// n linhas de texto menor). Superestima p/ servir de min-height e nunca clipar;
// a coluna ao lado usa o MÁXIMO da linha → IDA e RET alinham.
export const alturaCelula = (n) => (n <= 1 ? 40 : 10 + 17 * n);

// Altura da célula ATIVA (agrupando): chip (1) ou card de edição (sub-chips de 28px) + slot
// "solte aqui para juntar". O modal mede a LINHA por ela → a célula da outra coluna estica
// junto (SÓ a célula; o item visível dela mantém o tamanho natural, centralizado).
export const alturaAtiva = (n) => (n <= 1 ? 40 : 32 + 32 * n) + 45;

// Estratégia com grupo ativo: pairar OUTRA célula sobre o GRUPO não pré-visualiza troca —
// soltar nele JUNTA (não reordena). Qualquer outro alvo troca normalmente (rectSwapping), e
// arrastar o PRÓPRIO grupo também é normal. indexGrupo = -1 (sem grupo) → sempre rectSwapping.
const estrategiaComGrupo = (indexGrupo) => (args) =>
    (args.activeIndex !== indexGrupo && args.overIndex === indexGrupo) ? null : rectSwappingStrategy(args);

// NÃO re-anima o deslocamento após o reorder no drop: o item já foi p/ o lugar pelo transform do
// arraste; sem isto o dnd-kit RE-anima o movimento (replay), visível quando a troca é > 1 posição.
// Mantém a animação das outras mudanças de layout (add/remover slots vazios).
const semReplayNoDrop = ({ isSorting, wasDragging }) => (isSorting || wasDragging ? false : true);

/**
 * Coluna ordenável (dnd-kit / useSortable) com AGRUPAMENTO por célula.
 * `cells` = [{ id, itens: [] }] — `id` estável (o que o dnd-kit anima); `itens`
 * são trechos de linha única. 0 = vazia (placeholder), 1 = chip, >1 = grupo.
 *
 * Grupo tem 2 visões: COLAPSADO (depois do ✓) = um chip só com os textos
 * empilhados em linhas (fonte menor, sem rótulo/contagem); EDIÇÃO (agrupando via
 * (...) → Agrupar) = card com sub-chips removíveis + slot de juntar abaixo.
 *
 * Reordenar: arrastar uma célula sobre outra → TROCA de lugar (swap) as duas, sem
 * empurrar as demais — mantém o alinhamento posicional IDA × RET das outras linhas.
 * Juntar: arrastar OUTRA célula da mesma coluna pro slot da ativa → funde numa
 * célula (a arrastada esvazia=placeholder). O '\n' + soma só no Aplicar do modal.
 *
 * @param {Array}    cells     [{ id, itens: [] }]
 * @param {Function} setCells  setState das cells (recebe updater)
 * @param {Function} resumo    (trecho) => string  texto exibido
 * @param {Function} [corDe]   (trecho) => string  cor opcional (bolinha, só no chip único)
 * @param {Array}    [alturas] altura por índice (vinda do modal p/ alinhar colunas)
 * @param {string}   [rotuloGrupo='Grupo']  rótulo do card de edição de um grupo
 */
export default function ColunaArrastavel({ cells, setCells, resumo, corDe, alturas, rotuloGrupo = 'Grupo', lado = 'ida', arrastandoSub = false, agrupandoId = null, onAgrupar, travados = null, renderTravado = null, idsIda = null, onExcluirPar = null }) {
    // agrupandoId/onAgrupar são CONTROLADOS pelo modal → o grupo ativo é ÚNICO nas duas colunas
    // (agrupar numa desativa a da outra). null aqui = esta coluna não tem grupo ativo.
    const seq = useRef(0);
    // Índice do grupo ativo NESTA coluna (-1 = nenhum) → parametriza a estratégia: só o alvo
    // "grupo" fica sem preview de troca (soltar nele junta); o resto reordena normalmente.
    const indexGrupo = cells.findIndex(c => String(c.id) === String(agrupandoId));
    const estrategia = useMemo(() => estrategiaComGrupo(indexGrupo), [indexGrupo]);

    // Trata só os arrastes DESTA coluna que terminam NELA. Cruzar colunas (IDA↔RETORNO)
    // é tratado no modal, que detém o DndContext único compartilhado pelas duas colunas.
    const aoSoltar = (e) => {
        const { active, over } = e;
        if (!over) return;
        const arrast = active.data?.current || {};
        const alvo = over.data?.current || {};
        if (arrast.lado !== lado) return;            // arraste de outra coluna
        if (alvo.lado && alvo.lado !== lado) return; // soltou na outra coluna → modal trata
        if (arrast.par) return;                      // par internacional troca a LINHA → modal trata

        // ===== Sub-chip arrastado (dentro de um grupo em edição) =====
        if (arrast.tipo === 'subchip') {
            // Reordenar dentro do MESMO grupo (arrayMove nos itens da célula).
            if (alvo.tipo === 'subchip' && String(alvo.cellId) === String(arrast.cellId) && active.id !== over.id) {
                setCells(prev => {
                    const ci = prev.findIndex(c => String(c.id) === String(arrast.cellId));
                    if (ci < 0) return prev;
                    const de   = prev[ci].itens.findIndex(t => t._uid === active.id);
                    const para = prev[ci].itens.findIndex(t => t._uid === over.id);
                    if (de < 0 || para < 0) return prev;
                    const next = prev.map(c => ({ ...c, itens: [...c.itens] }));
                    next[ci].itens = arrayMove(next[ci].itens, de, para);
                    return next;
                });
                return;
            }
            // Soltou de volta no próprio grupo (sobre o card) → nada a fazer.
            if (alvo.tipo === 'cell' && String(over.id) === String(arrast.cellId)) return;
            // Qualquer outro destino → TIRAR do grupo. Se for uma célula vazia, vai p/ ela.
            extrairSubchip(arrast.cellId, active.id, alvo.tipo === 'cell' ? over.id : null);
            return;
        }

        // Funde a célula arrastada num item ATIVO. Aceita 2 alvos: o slot tracejado
        // (tipo 'nest') OU o PRÓPRIO item ativo (tipo 'cell' com id === agrupandoId) —
        // a área dropável é o item inteiro, não o sub-chip. Vale p/ grupo e p/ chip
        // único ativo (1 item). closestCorners já ignora sub-chips quando se
        // arrasta uma célula (ver `colisao`).
        const alvoMerge = alvo.tipo === 'nest' ? alvo.alvoId
            : (alvo.tipo === 'cell' && String(over.id) === String(agrupandoId)) ? over.id : null;
        if (alvoMerge != null && String(alvoMerge) !== String(active.id)) {
            setCells(prev => {
                const di = prev.findIndex(c => c.id === active.id);
                const ai = prev.findIndex(c => String(c.id) === String(alvoMerge));
                if (di < 0 || ai < 0) return prev;
                const next = prev.map(c => ({ ...c, itens: [...c.itens] }));
                next[ai].itens.push(...next[di].itens);   // grupo-no-slot achata
                next[di].itens = [];                       // arrastada vira placeholder
                return next;
            });
            if (String(agrupandoId) === String(active.id) && onAgrupar) onAgrupar(active.id); // arrastada era a ativa → desativa
            return;
        }

        // Troca de lugar (NÃO empurra a lista): só as duas células trocam de posição — preserva
        // o alinhamento posicional IDA × RET. Vale também COM grupo ativo: soltar NO grupo junta
        // (tratado acima); qualquer outro alvo reordena normalmente.
        if (active.id !== over.id) {
            setCells(prev => {
                const from = prev.findIndex(c => c.id === active.id);
                const to   = prev.findIndex(c => c.id === over.id);
                if (from < 0 || to < 0) return prev;
                const next = [...prev];
                [next[from], next[to]] = [next[to], next[from]];
                return next;
            });
        }
    };

    // Sem DndContext próprio: ouve o contexto único do modal (que abrange IDA e RETORNO).
    useDndMonitor({ onDragEnd: aoSoltar });

    const agrupar   = (id) => onAgrupar && onAgrupar(id);
    const excluir = (id) => {
        setCells(prev => prev.map(c => (c.id === id ? { ...c, itens: [] } : c)));
        if (String(agrupandoId) === String(id) && onAgrupar) onAgrupar(id); // excluiu a ativa → desativa (toggle)
    };
    // Tira um trecho do grupo → primeira célula vazia da coluna, ou nova célula no fim.
    const desagrupar = (cellId, idx) => {
        setCells(prev => {
            const ci = prev.findIndex(c => c.id === cellId);
            if (ci < 0 || prev[ci].itens.length <= 1) return prev;
            const next = prev.map(c => ({ ...c, itens: [...c.itens] }));
            const [trecho] = next[ci].itens.splice(idx, 1);
            const vi = next.findIndex(c => c.itens.length === 0);
            if (vi >= 0) next[vi] = { ...next[vi], itens: [trecho] };
            else next.push({ id: `x-${seq.current++}`, itens: [trecho] });
            return next;
        });
    };
    // Extrair via DRAG: tira o trecho (por _uid) do grupo. Se `destinoId` for uma
    // célula vazia, vai p/ ela (drop posicional); senão cai na 1ª vazia / célula nova.
    const extrairSubchip = (cellId, uid, destinoId) => {
        setCells(prev => {
            const ci = prev.findIndex(c => String(c.id) === String(cellId));
            if (ci < 0 || prev[ci].itens.length <= 1) return prev;
            const idx = prev[ci].itens.findIndex(t => t._uid === uid);
            if (idx < 0) return prev;
            const next = prev.map(c => ({ ...c, itens: [...c.itens] }));
            const [trecho] = next[ci].itens.splice(idx, 1);
            let di = -1;
            if (destinoId != null) {
                const d = next.findIndex(c => String(c.id) === String(destinoId));
                if (d >= 0 && d !== ci && next[d].itens.length === 0) di = d;
            }
            if (di < 0) di = next.findIndex(c => c.itens.length === 0);
            if (di >= 0) next[di] = { ...next[di], itens: [trecho] };
            else next.push({ id: `x-${seq.current++}`, itens: [trecho] });
            return next;
        });
    };

    return (
        <SortableContext items={cells.map(c => c.id)} strategy={estrategia}>
            <div className="d-flex flex-column gap-2">
                {cells.map((c, i) => (
                    <Celula key={c.id} id={c.id} itens={c.itens} lado={lado}
                        altura={(alturas && alturas[i]) || alturaCelula(c.itens.length)}
                        agrupando={String(agrupandoId) === String(c.id)}
                        arrastandoSub={arrastandoSub}
                        resumo={resumo} corDe={corDe} rotuloGrupo={rotuloGrupo}
                        travado={!!travados && travados.has(String(c.id))}
                        conteudoTravado={renderTravado ? (acoes) => renderTravado(c, i, lado, acoes) : null}
                        indice={i} idsIda={idsIda}
                        onExcluirPar={onExcluirPar}
                        onAgrupar={() => agrupar(c.id)}
                        onExcluir={() => excluir(c.id)}
                        onDesagrupar={(idx) => desagrupar(c.id, idx)} />
                ))}
            </div>
        </SortableContext>
    );
}

// Conteúdo do DragOverlay — renderizado no modal, que detém o DndContext único.
// `resumo` vem do lado do item arrastado (ativo.lado).
export function OverlayConteudo({ ativo, resumo, corDe }) {
    if (!ativo) return null;
    if (ativo.tipo === 'subchip') {
        return ativo.trecho ? <SubChipVisual item={ativo.trecho} resumo={resumo} corDe={corDe} overlay /> : null;
    }
    if (ativo.cell && ativo.cell.itens.length > 0) {
        return ativo.cell.itens.length > 1
            ? <MultiChip itens={ativo.cell.itens} resumo={resumo} overlay />
            : <Chip item={ativo.cell.itens[0]} resumo={resumo} corDe={corDe} overlay />;
    }
    return null;
}

function Celula({ id, itens, lado, altura, agrupando, arrastandoSub, resumo, corDe, rotuloGrupo, travado, conteudoTravado, indice, idsIda, onAgrupar, onExcluir, onExcluirPar, onDesagrupar }) {
    const vazio = itens.length === 0;
    const grupo = itens.length > 1;
    const editando = agrupando && grupo;   // card de edição só quando ativo E grupo
    // Travada (par internacional): as duas metades andam juntas. FECHADO, quem desenha o par é o
    // modal, que tem as duas e junta num card só; a metade da IDA é a alça — arrasta e recebe outro
    // par, e o modal troca a LINHA inteira nas duas colunas (o `colisao` só a deixa mirar na coluna
    // da IDA); a do RETORNO fica inerte, embaixo do card. EM EDIÇÃO (Agrupar), o par se abre nas
    // duas metades, cada uma na sua coluna: elas não arrastam — o par não se desmancha —, mas
    // recebem, para juntar trecho ao itinerário. Nenhuma sai da lista: é o índice que pareia.
    const parFechado = travado && !agrupando;
    const alcaDoPar = parFechado && lado === 'ida';
    const { attributes, listeners, setNodeRef, transform, transition, isDragging, isOver } = useSortable({
        id, data: { tipo: 'cell', lado, par: parFechado },
        disabled: travado
            ? (agrupando ? { draggable: true, droppable: false } : (alcaDoPar ? false : { draggable: true, droppable: true }))
            : (vazio ? { draggable: true, droppable: false } : false),
        animateLayoutChanges: semReplayNoDrop,
    });
    // Preview da troca de LINHA do par na coluna do RETORNO. O dnd-kit só desloca itens do
    // SortableContext do item arrastado, e o par se arrasta pela IDA. Aqui a célula do RETORNO
    // pede ao dnd-kit o deslocamento da célula da IDA da MESMA linha: a mesma estratégia
    // (`rectSwapping`), com o `active` e o `over` do contexto dele e os retângulos que ele mediu
    // da coluna da IDA. A linha anda como um bloco só, sem conta nem estado nossos. A transição
    // é a do próprio `useSortable`, que ele entrega a toda célula enquanto existe um arraste.
    const { active, over, activeNodeRect, droppableRects } = useDndContext();
    let espelho = null;
    if (idsIda && active?.data?.current?.par && over) {
        const de = idsIda.indexOf(active.id);
        const para = idsIda.indexOf(over.id);
        if (de >= 0 && para >= 0 && de !== para && (indice === de || indice === para)) {
            espelho = rectSwappingStrategy({
                rects: idsIda.map(i => droppableRects.get(i)), activeNodeRect,
                activeIndex: de, overIndex: para, index: indice,
            });
        }
    }
    // rectSwapping escala o item p/ o tamanho do alvo quando as células têm tamanhos diferentes
    // (chip único × grupo) → preview deformado/gigante. Mantém só a translação (scaleX/scaleY = 1)
    // p/ o item conservar o tamanho real ao arrastar.
    const deslocamento = espelho ? { x: 0, y: espelho.y } : transform;
    const style = {
        transform: CSS.Transform.toString(deslocamento ? { ...deslocamento, scaleX: 1, scaleY: 1 } : null),
        transition,
        opacity: isDragging ? 0.4 : 1,
    };

    // A linha inteira (`altura`) é do INVÓLUCRO, que centraliza; o nó do dnd-kit é o de dentro,
    // do tamanho do item. É o que mantém o item no meio da linha SEM desencontrar a geometria:
    // o dnd-kit mede o nó dele e posiciona tudo pelo topo dessa medida — o preview da troca, o
    // overlay em relação ao cursor e a animação da soltura. Medindo a caixa alta, o item era
    // desenhado no topo dela e o overlay saía deslocado de onde o cursor pegou.
    if (parFechado) {
        // O (⋯) do par vai dentro do card. O Excluir dele tira a OPÇÃO inteira — as duas metades —
        // e quem faz é o modal, que tem as duas colunas.
        const acoesPar = alcaDoPar ? <CelulaAcoes agrupando={false} onAgrupar={onAgrupar} onExcluir={onExcluirPar ? () => onExcluirPar(id) : null} /> : null;
        return (
            <div className="d-flex flex-column justify-content-center" style={{ minHeight: altura }}>
                {/* O card atravessa para a área da coluna do RETORNO, e ela vem depois no DOM: sem
                    posição própria, quem ganhava o clique na metade direita era a coluna (e a lista
                    dela), transparentes, e o card só arrastava pela metade da ida. Posicionado e
                    acima, ele recebe o clique em qualquer ponto, o (⋯) incluso. No arraste ele
                    desce, para as linhas que deslizam no preview passarem por cima do fantasma. */}
                <div ref={setNodeRef}
                    style={alcaDoPar
                        ? { ...style, cursor: isDragging ? 'grabbing' : 'grab', position: 'relative', zIndex: isDragging ? undefined : 1 }
                        : style}
                    {...(alcaDoPar ? { ...attributes, ...listeners } : {})}>
                    {conteudoTravado ? conteudoTravado(acoesPar) : null}
                </div>
            </div>
        );
    }
    if (vazio) {
        // Realce só faz sentido ao extrair um SUB-CHIP (soltar numa célula vazia);
        // arrastando uma célula normal (reordenar), o realce vira ruído.
        return (
            <div className="d-flex flex-column justify-content-center" style={{ minHeight: altura }}>
                <div ref={setNodeRef} style={style}>
                    <Vazio over={isOver && arrastandoSub} />
                </div>
            </div>
        );
    }
    const acoes = <CelulaAcoes agrupando={agrupando} onAgrupar={onAgrupar} onExcluir={onExcluir} />;
    // OUTRO draggable pairando sobre a célula ATIVA → vai JUNTAR: realce azul aplicado NO ITEM
    // (não na célula toda, que engloba o slot). `!isDragging` evita o auto-realce quando é a
    // própria célula ativa que está sendo arrastada (reordenar).
    const juntarHover = agrupando && isOver && !isDragging;
    return (
        <div className="d-flex flex-column justify-content-center" style={{ minHeight: altura }}>
            {/* Slot "juntar" DENTRO do nó medido → card + slot são um bloco só para o dnd-kit, e
                a altura deles (alturaAtiva) é a que a linha assume nas duas colunas. */}
            <div ref={setNodeRef} style={{ ...style, cursor: travado ? 'default' : (isDragging ? 'grabbing' : 'grab') }}
                {...attributes} {...listeners}>
                {editando
                    ? <CardEdicao cellId={id} lado={lado} itens={itens} resumo={resumo} corDe={corDe} rotulo={rotuloGrupo} acoes={acoes} onDesagrupar={onDesagrupar} realce={juntarHover} />
                    : grupo
                        ? <MultiChip itens={itens} resumo={resumo} acoes={acoes} realce={juntarHover} />
                        : <Chip item={itens[0]} resumo={resumo} corDe={corDe} acoes={acoes} realce={juntarHover} />}
                {agrupando && <SlotJuntar cellId={id} lado={lado} />}
            </div>
        </div>
    );
}

// Slot droppable que aparece sob uma célula ativa: soltar aqui = juntar.
function SlotJuntar({ cellId, lado }) {
    const { setNodeRef, isOver } = useDroppable({ id: `nest::${cellId}`, data: { tipo: 'nest', alvoId: cellId, lado } });
    return (
        <div ref={setNodeRef} className="d-flex align-items-center justify-content-center gap-2 mt-1 py-2"
            style={{
                border: `1.5px dashed ${isOver ? BRAND_BLUE : '#c8ddf5'}`,
                background: isOver ? '#e8f2ff' : '#f8fbff',
                borderRadius: 10,
                color: isOver ? BRAND_BLUE : '#9db8d9',
                fontSize: 12,
                transition: 'background 120ms, border-color 120ms',
            }}>
            <FaLink size={11} style={{ opacity: isOver ? 1 : 0.6 }} />
            <span className="fw-semibold">solte aqui para juntar</span>
        </div>
    );
}

// A caixa tracejada mantém o tamanho natural; quem estica para acompanhar a linha é o invólucro
// da célula, que a centraliza. Importa quando a linha infla p/ acompanhar um grupo em edição na
// outra coluna.
function Vazio({ over }) {
    return (
        <div className="d-flex align-items-center justify-content-center px-2"
            style={{ minHeight: 40, border: `1px dashed ${over ? BRAND_BLUE : BRAND_BORDER}`, borderRadius: 10, color: '#b9c4d6', fontSize: 13, background: over ? BRAND_SKY : 'transparent' }}>
            —
        </div>
    );
}

// Chip de trecho simples (célula de 1 trecho).
function Chip({ item, resumo, corDe, acoes, overlay, realce }) {
    const cor = corDe ? corDe(item) : null;
    const texto = resumo(item);
    return (
        <div className="d-flex align-items-center gap-2 px-2"
            style={{ height: 40, background: BRAND_SKY, border: `1px solid ${realce ? BRAND_BLUE : BRAND_BORDER}`, borderRadius: 10, fontSize: 13, color: BRAND_NAVY, boxShadow: realce ? REALCE_SHADOW : (overlay ? '0 8px 20px rgba(31,60,136,0.18)' : 'none') }}>
            <FaGripVertical size={12} style={{ color: '#9fb3d4', flexShrink: 0 }} />
            {cor && <span style={{ width: 8, height: 8, borderRadius: '50%', background: cor, flexShrink: 0 }} />}
            <span className="text-truncate" style={{ minWidth: 0 }} title={typeof texto === 'string' ? texto : undefined}>{texto}</span>
            {acoes && <span className="ms-auto d-flex align-items-center" style={{ flexShrink: 0 }} onPointerDown={e => e.stopPropagation()}>{acoes}</span>}
        </div>
    );
}

// Grupo COLAPSADO: um chip só, cada trecho numa linha (fonte menor). Sem rótulo/contagem.
function MultiChip({ itens, resumo, acoes, overlay, realce }) {
    return (
        <div className="d-flex align-items-center gap-2 px-2 py-1"
            style={{ background: BRAND_SKY, border: `1px solid ${realce ? BRAND_BLUE : BRAND_BORDER}`, borderRadius: 10, color: BRAND_NAVY, boxShadow: realce ? REALCE_SHADOW : (overlay ? '0 8px 20px rgba(31,60,136,0.18)' : 'none') }}>
            <FaGripVertical size={12} style={{ color: '#9fb3d4', flexShrink: 0 }} />
            <div className="d-flex flex-column" style={{ minWidth: 0, flex: 1 }}>
                {itens.map((t, i) => {
                    const texto = resumo(t);
                    return (
                        <span key={t._id ?? i} className="text-truncate"
                            style={{ minWidth: 0, fontSize: 11.5, lineHeight: '16px' }}
                            title={typeof texto === 'string' ? texto : undefined}>{texto}</span>
                    );
                })}
            </div>
            {acoes && <span className="ms-auto d-flex align-items-center" style={{ flexShrink: 0 }} onPointerDown={e => e.stopPropagation()}>{acoes}</span>}
        </div>
    );
}

// Grupo em EDIÇÃO (agrupando): card com cabeçalho, sub-chips arrastáveis (reordenar
// dentro do grupo ou arrastar p/ fora = extrair) com × p/ desagrupar.
function CardEdicao({ cellId, lado, itens, resumo, corDe, rotulo, acoes, onDesagrupar, realce }) {
    return (
        <div style={{ background: BRAND_SKY, border: `1px solid ${realce ? BRAND_BLUE : BRAND_BORDER}`, borderRadius: 10, padding: 4, boxShadow: realce ? REALCE_SHADOW : 'none' }}>
            <div className="d-flex align-items-center gap-2 px-1" style={{ height: 18 }}>
                <FaGripVertical size={12} style={{ color: '#9fb3d4', flexShrink: 0 }} />
                <span className="fw-semibold text-uppercase" style={{ fontSize: 10, color: BRAND_BLUE, letterSpacing: '0.04em' }}>{rotulo} · {itens.length}</span>
                {acoes && <span className="ms-auto d-flex align-items-center" style={{ flexShrink: 0 }} onPointerDown={e => e.stopPropagation()}>{acoes}</span>}
            </div>
            <SortableContext items={itens.map(t => t._uid)} strategy={verticalListSortingStrategy}>
                <div className="d-flex flex-column gap-1 mt-1">
                    {itens.map((t, i) => (
                        <SubChip key={t._uid ?? i} cellId={cellId} lado={lado} item={t} resumo={resumo} corDe={corDe}
                            onRemover={onDesagrupar ? () => onDesagrupar(i) : null} />
                    ))}
                </div>
            </SortableContext>
        </div>
    );
}

// Sub-chip ARRASTÁVEL dentro do card (nested useSortable). stopPropagation no
// pointerdown impede a célula-mãe de iniciar drag — só o sub-chip se move.
function SubChip({ cellId, lado, item, resumo, corDe, onRemover }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: item._uid, data: { tipo: 'subchip', cellId, lado },
    });
    const style = {
        transform: CSS.Transform.toString(transform), transition,
        opacity: isDragging ? 0.4 : 1, cursor: isDragging ? 'grabbing' : 'grab',
    };
    const aoApontar = (e) => { e.stopPropagation(); listeners && listeners.onPointerDown && listeners.onPointerDown(e); };
    return (
        <div ref={setNodeRef} style={style} {...attributes} {...listeners} onPointerDown={aoApontar}>
            <SubChipVisual item={item} resumo={resumo} corDe={corDe} onRemover={onRemover} />
        </div>
    );
}

// Visual do sub-chip (reusado no card arrastável e no DragOverlay).
function SubChipVisual({ item, resumo, corDe, onRemover, overlay }) {
    const cor = corDe ? corDe(item) : null;
    const texto = resumo(item);
    return (
        <div className="d-flex align-items-center gap-2 px-2"
            style={{ height: 28, background: '#fff', border: `1px solid ${BRAND_BORDER}`, borderRadius: 8, fontSize: 12, color: BRAND_NAVY, boxShadow: overlay ? '0 8px 20px rgba(31,60,136,0.18)' : 'none' }}>
            <FaGripVertical size={11} style={{ color: '#9fb3d4', flexShrink: 0 }} />
            {cor && <span style={{ width: 7, height: 7, borderRadius: '50%', background: cor, flexShrink: 0 }} />}
            <span className="text-truncate" style={{ minWidth: 0 }} title={typeof texto === 'string' ? texto : undefined}>{texto}</span>
            {onRemover && (
                <button type="button" title="Tirar do grupo"
                    className="btn btn-sm p-0 ms-auto border-0 bg-transparent d-flex align-items-center"
                    style={{ color: BRAND_NAVY, opacity: 0.5, flexShrink: 0 }}
                    onPointerDown={e => e.stopPropagation()}
                    onClick={e => { e.stopPropagation(); onRemover(); }}>
                    <FaTimes size={11} />
                </button>
            )}
        </div>
    );
}

// Kebab (...) → menu (Agrupar / Excluir). Agrupando, vira o ✓ que conclui.
function CelulaAcoes({ agrupando, onAgrupar, onExcluir }) {
    const [aberto, setAberto] = useState(false);
    if (agrupando) {
        return (
            <button type="button" title="Concluir agrupamento"
                className="btn btn-sm p-0 border-0 bg-transparent d-flex align-items-center"
                style={{ color: BRAND_BLUE }}
                onPointerDown={e => e.stopPropagation()}
                onClick={e => { e.stopPropagation(); onAgrupar(); }}>
                <FaCheck size={13} />
            </button>
        );
    }

    // O menu vai para o BODY, por portal. Ele nasce dentro da área que rola no modal, e ela
    // corta o que passa da borda — a lista aparecia pela metade. Posicionar contra a janela
    // (`strategy: fixed`) não resolveu: o modal e a célula arrastável criam blocos de
    // referência próprios, e o menu ia parar longe do botão. No portal não há ancestral para
    // recortar nem para deslocar, e é o mesmo caminho que o (i) da observação já usa.
    //
    // Controlado porque escolher uma ação tem de FECHAR: solto, o popover só fecha por clique
    // fora, e a lista ficava aberta sobre a célula que acabou de mudar.
    const item = (cor, Icone, rotulo, acao) => (
        <button type="button"
            className="btn btn-sm w-100 d-flex align-items-center gap-2 justify-content-start"
            style={{ color: cor, fontSize: 13, padding: '6px 10px', borderRadius: 8 }}
            onPointerDown={e => e.stopPropagation()}
            onClick={e => { e.stopPropagation(); setAberto(false); acao(); }}>
            <Icone size={11} /> {rotulo}
        </button>
    );

    return (
        <OverlayTrigger trigger="click" placement="bottom-end" rootClose container={document.body}
            show={aberto} onToggle={setAberto} overlay={(
                <Popover id="acoes-trecho" style={{ maxWidth: 180 }}>
                    <Popover.Content style={{ padding: 4 }} onPointerDown={e => e.stopPropagation()}>
                        {item(BRAND_NAVY, FaLayerGroup, 'Agrupar', onAgrupar)}
                        {onExcluir && item('#c0392b', FaTrash, 'Excluir', onExcluir)}
                    </Popover.Content>
                </Popover>
            )}>
            <button type="button" title="Ações"
                className="btn btn-sm p-0 border-0 bg-transparent d-flex align-items-center"
                style={{ color: BRAND_NAVY, opacity: 0.6 }}
                onPointerDown={e => e.stopPropagation()}>
                <FaEllipsisV size={13} />
            </button>
        </OverlayTrigger>
    );
}
